import { FC, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  ExpandedTable,
  IButton,
  IconButton,
  Layout,
  StoredPagination,
  SearchFormField,
  ITooltip,
  ConfirmModal,
} from "../../ui-kit";
import { TrashIco } from "../../assets/customIcons";
import usersStore from "../../store/users";
import authApi from "../../api/Auth";

import styles from "./Users.module.scss";
import { convertDate } from "../../utils/helpers/data.helpers";
import UserForm from "./form/user";
import UsersFilter from "./usersFilter";
import { Tag } from "antd";
import Paths from "../../constants/PathsBreadcrumbs";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { useHandleForm } from "../../hooks/useHandleForm";
import { useSearch } from "../../hooks/useSearch";
import { useStatusMessage } from "../../hooks/useStatusMessage";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import Constants from "../../constants/Common";

const dateNow = new Date();
const PERMISSION_ADMIN_ID = 99014;

const Users: FC = observer(() => {
  const {
    titleIcons: { trash },
  } = Constants;
  const {
    usersData,
    paginator,
    search,
    sorter,
    getUsersList,
    filter,
    userParamsRowKeys,
    isShowAddForm,
  } = usersStore;

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    authApi.checkPermission(PERMISSION_ADMIN_ID)
      .then(setIsAdmin)
      .catch(console.error);
  }, []);

  useEffect(() => {
    getUsersList();
  }, [
    paginator.page,
    paginator.perPage,
    search.search.text,
    sorter.changingKey,
  ]);

  useEffect(() => {
    if (paginator.page !== 1) {
      paginator.onPerPageChange(paginator.perPage);
    } else {
      getUsersList();
    }
  }, [usersStore.filter.filterItems]);

  useEffect(() => {
    usersStore.getRolesList();
  }, []);

  useEffect(() => {
    if (!usersStore.userFormIsSending) {
    }
  }, [usersStore.userFormIsSending]);

  useBreadcrumbs(Paths.adminUsers);

  useStatusMessage(usersStore.userStateStatus);

  const deleteData = (e, id) => {
    e.stopPropagation();
    usersStore.deleteUser(id);
  };

  const {
    confirmModalData,
    toggleConfirmModal,
    closeConfirmModal,
    confirmDelete: confirmUndelete,
  } = useDeleteConfirm(usersStore.unDeleteUser);

  const getStatusTag = (_, record) => {
    const params = {
      color: "green",
      children: "Активен",
    };
    if (record?.isDisabled && !record?.lockoutEnd) {
      params.color = "yellow";
      params.children = "Заблокирован";
    }
    if (record?.isDisabled && new Date(record?.lockoutEnd) > dateNow) {
      params.color = "yellow";
      params.children = `Заблокирован до ${convertDate(record?.lockoutEnd)}`;
    }
    if (record?.isDeleted) {
      return (
        <>
          <Tag color="red">Удален</Tag>
          {isAdmin && (
            <IButton
              variant="outlined"
              className={styles.UndeleteUserButton}
              onClick={() => toggleConfirmModal({ show: true, id: record.userId })}
            >
              Отменить удаление
            </IButton>
          )}
        </>
      );
    }
    return <Tag {...params}></Tag>;
  };

  const columns = [
    {
      title: "Логин",
      dataIndex: "userName",
      key: "userName",
      sortable: true,
    },
    {
      title: "Статус",
      dataIndex: "state",
      key: "state",
      render: getStatusTag,
    },
    {
      title: "Имя",
      dataIndex: "fullName",
      key: "fullName",
      sortable: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sortable: true,
    },
    {
      title: "Телефон",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      sortable: true,
    },
    {
      title: "Роль",
      dataIndex: "userGroupName",
      key: "userGroupName",
      sortable: true,
    },
    {
      title: "Описание роли",
      dataIndex: "userGroupDescription",
      key: "userGroupDescription",
      sortable: true,
    },
    {
      title: "Организация",
      dataIndex: "organizationName",
      key: "organizationName",
      sortable: true,
    },
    {
      title: "Дата последнего входа",
      dataIndex: "lastLoginDt",
      key: "lastLoginDt",
      render: (lastLoginDt) => (lastLoginDt ? convertDate(lastLoginDt) : ""),
      sortable: true,
    },
    {
      title: "Дата последней активности",
      dataIndex: "lastActivityDt",
      key: "lastActivityDt",
      render: (lastActivityDt) =>
        lastActivityDt ? convertDate(lastActivityDt) : "",
      sortable: true,
    },
    {
      title: " ",
      key: "delete",
      render: (text, record, index) => (
        <ITooltip title={trash} placement="right">
          <TrashIco onClick={(e) => deleteData(e, record.userId)} />
        </ITooltip>
      ),
      className: "delete-cell",
    },
  ];
  const searchForm = useHandleForm(null, { text: "" }, { text: false });
  const {
    handleOnClear: handleClearSearch,
    handleOnChange: handleChangeSearch,
    onFindClick,
  } = useSearch({
    search: search,
    delay: 1000,
    searchForm: searchForm,
    searchKey: "text",
    onFindAction: getUsersList,
  });

  return (
    <Layout type="white">
      <ConfirmModal
        title="Восстановление записи"
        message="Вы действительно хотите отменить удаление и восстановить запись?"
        open={confirmModalData.show}
        onClose={closeConfirmModal}
        onConfirm={confirmUndelete}
      />
      <div className={styles.Filter}>
        <div className={styles.fields}>
          <UsersFilter filter={filter} />
        </div>
        <div className={styles.controls}>
          <SearchFormField
            name="searchText"
            label="Поиск пользователя (Фамилия, email, телефон, логин)"
            type="text"
            className={styles.UsersSearch}
            value={searchForm.watchAllField.text}
            isFocused={searchForm.isFocused.text}
            onFocus={searchForm.handleFocus}
            onBlur={searchForm.handleBlur}
            onClear={handleClearSearch}
            onChange={handleChangeSearch}
            onFindClick={onFindClick}
          />
        </div>
      </div>
      <div className={styles.UsersPage}>
        <ExpandedTable
          loading={usersStore.isLoading.value}
          rowKeys={userParamsRowKeys.keys}
          setRowKeys={userParamsRowKeys.setKeys}
          columns={columns}
          sort={sorter}
          data={usersData}
          rowExpandable={(record) => !record?.isDeleted}
        >
          <UserForm />
        </ExpandedTable>
        <StoredPagination {...paginator} />

        <IconButton
          size="medium"
          typeIcon={isShowAddForm.value ? "minus" : "add"}
          onClick={(e) => isShowAddForm.toggle()}
          className={styles.UsersAddButton}
        >
          {isShowAddForm.value ? "Отменить" : "Добавить пользователя"}
        </IconButton>
        {isShowAddForm.value && <UserForm />}
      </div>
    </Layout>
  );
});

export default Users;
